import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { OverflownText } from 'components/OverflownText'
import { useTableLinkVariant } from './TableLinkStyleContext'

/**
 * Cell with a click-triggered popover that lists every item on its own line
 * and scrolls when the list is long. Used for chain summary cells
 * (state / city / DMA / zip) whose original react-bootstrap tooltip blew up
 * the viewport with a giant single-paragraph string. The "…" button keeps the
 * popover open while scrolling without losing context of the source row.
 *
 * Outside the DS2026 surface (TableLinkStyleProvider variant="plain") the cell
 * falls back to the original OverflownText + tooltip render so non-targets
 * pages stay unchanged.
 */
export function OverflownScrollList({
  items,
  previewLimit = 25,
  separator = ', ',
}: {
  items: string[]
  previewLimit?: number
  separator?: string
}) {
  const variant = useTableLinkVariant()

  if (!items || items.length === 0) return <>-</>

  const preview = items.slice(0, previewLimit).join(separator)

  if (variant !== 'plain') {
    const tooltipText =
      items.length > previewLimit
        ? `${preview}, ... (${items.length - previewLimit} more)`
        : preview
    return (
      <OverflownText tooltipText={tooltipText} maxLines={2}>
        {preview}
      </OverflownText>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-scroll-list-trigger
          aria-label={`View all ${items.length} items`}
          title={preview}
          // Opening the popover should never also fire the row's onRowClick
          // (e.g. the Rebate offers table navigates on row click).
          onClick={(e) => e.stopPropagation()}
          className="w-full min-w-0 max-w-full cursor-pointer truncate text-left hover:text-neutral-900"
        >
          {preview}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="center"
        sideOffset={-32}
        className="max-h-80 w-64 overflow-y-auto !p-3"
      >
        {items.length > 1 && (
          <div className="mb-2 text-xs font-medium text-neutral-500">
            {items.length} items
          </div>
        )}
        <ul className="space-y-1 text-sm text-neutral-700">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
